import { useCallback, useEffect, useMemo, useRef } from 'react';
import debounce from 'lodash/debounce';

import { useBreakpoint } from 'Context/BreakpointsContext/BreakpointsContext';

import { GAP_SIZE, SIMILAR_CONTAINER_MIN_HEIGHT } from './VehicleDetailsRedesign.const';
import { getSections } from './VehicleDetailsRedesign.helpers';
import type { UseLayoutCalc, UseSections, UseSectionsProps } from './VehicleDetailsRedesign.types';

export const useSections = ({
	hasAdditionalImages,
	hasEarlyDocuments,
	hasRelistDetails,
	isExLeaseVehicle,
	isMobilizeVehicle,
	isTrade,
}: UseSectionsProps): UseSections => {
	const shouldDisplayTraderDealerInfo = Boolean(isTrade) && !isExLeaseVehicle;
	const shouldDisplayRelistDetails = Boolean(hasRelistDetails);
	const shouldDisplayEarlyDocuments = Boolean(hasEarlyDocuments);

	const sections = useMemo(() => {
		return getSections({
			includeAdditionalInfo: hasAdditionalImages,
			includeEarlyDocuments: shouldDisplayEarlyDocuments,
			includeExLease: isExLeaseVehicle,
			includeMobilize: isMobilizeVehicle,
			includeMobilizeDamage: isMobilizeVehicle,
			includeRelistDetails: shouldDisplayRelistDetails,
			includeSummary: false,
			includeTraderDealerInfo: shouldDisplayTraderDealerInfo,
		});
	}, [
		isMobilizeVehicle,
		hasAdditionalImages,
		shouldDisplayEarlyDocuments,
		isExLeaseVehicle,
		shouldDisplayRelistDetails,
		shouldDisplayTraderDealerInfo,
	]);

	const quickLinks = useMemo(() => {
		return getSections({
			includeAdditionalInfo: hasAdditionalImages,
			includeEarlyDocuments: shouldDisplayEarlyDocuments,
			includeExLease: isExLeaseVehicle,
			includeMobilize: isMobilizeVehicle,
			includeMobilizeDamage: isMobilizeVehicle,
			includeRelistDetails: shouldDisplayRelistDetails,
			includeSummary: true,
			includeTraderDealerInfo: shouldDisplayTraderDealerInfo,
		});
	}, [
		isMobilizeVehicle,
		hasAdditionalImages,
		shouldDisplayEarlyDocuments,
		isExLeaseVehicle,
		shouldDisplayRelistDetails,
		shouldDisplayTraderDealerInfo,
	]);

	return {
		quickLinks,
		sections,
	};
};

export const useLayoutCalc = (): UseLayoutCalc => {
	const similarRef = useRef<HTMLDivElement>(null);
	const contentRef = useRef<HTMLDivElement>(null);
	const biddingRef = useRef<HTMLDivElement>(null);
	const stickyRef = useRef<HTMLDivElement>(null);

	const {
		minWidth: { breakpointTablet: isDesktop },
	} = useBreakpoint();

	const setPosition = useCallback(() => {
		if (!biddingRef.current || !similarRef.current || !contentRef.current || !stickyRef.current || !isDesktop) return;

		const biddingHeight = biddingRef.current.clientHeight;
		const similarHeight = similarRef.current.clientHeight;
		const stickyOffset = stickyRef.current.offsetTop;
		const contentOffset = Math.max(contentRef.current.offsetTop, stickyOffset + biddingHeight + GAP_SIZE);
		const isBiddingBellowContent = biddingRef.current.offsetTop > contentRef.current.offsetTop;

		const biddingMarginBottom = isBiddingBellowContent
			? similarHeight + GAP_SIZE
			: Math.max(SIMILAR_CONTAINER_MIN_HEIGHT + GAP_SIZE, similarHeight + GAP_SIZE);
		const similarTop = biddingHeight + GAP_SIZE * 2;
		const similarMarginTop = stickyOffset + biddingHeight + biddingMarginBottom - contentOffset + GAP_SIZE;

		biddingRef.current.style.marginBottom = `${biddingMarginBottom}px`;
		similarRef.current.style.top = `${similarTop}px`;
		similarRef.current.style.marginTop = `-${similarMarginTop}px`;
	}, [isDesktop]);

	const resetPosition = useCallback(() => {
		if (!biddingRef.current || !similarRef.current) return;

		biddingRef.current.style.marginBottom = '0px';
		similarRef.current.style.top = '0px';
		similarRef.current.style.marginTop = '0px';
	}, []);

	const setPositionDebounced = useMemo(() => {
		return debounce(setPosition, 100, { maxWait: 100 });
	}, [setPosition]);

	useEffect(() => {
		const layoutResizeObserver = new ResizeObserver(setPosition);

		if (biddingRef.current) {
			layoutResizeObserver.observe(biddingRef.current);
		}

		if (similarRef.current) {
			layoutResizeObserver.observe(similarRef.current);
		}

		window.addEventListener('scroll', setPositionDebounced);
		window.addEventListener('resize', setPosition);

		return () => {
			layoutResizeObserver?.disconnect();
			window.removeEventListener('scroll', setPositionDebounced);
			window.removeEventListener('resize', setPosition);
		};
	}, [setPosition, setPositionDebounced]);

	useEffect(() => {
		if (isDesktop) setPosition();
		else resetPosition();
	}, [isDesktop, resetPosition, setPosition]);

	return { biddingRef, contentRef, isDesktop, similarRef, stickyRef };
};
